/* @flow */
/* eslint-env es6,browser */

//set global variable needed
var __IndexRequestData = window._IndexRequestData || {};
var _cygnus_index_args = window.cygnus_index_args || {};
var _cygnus_index_start: () => string = window.cygnus_index_start || (() => '');
window._IndexRequestData = __IndexRequestData;
window.cygnus_index_args = _cygnus_index_args;
window.cygnus_index_start = _cygnus_index_start;

import * as u from '../utils';
import bidbase from './bidbase';
import bm from '../bidmanager';

/**
 * @module bidders/index
 */
/**
 * @global
 * @typedef {object} indexConfig
 * @description extends {@link hbConfig}
 * @property {number} id
 */
/**
 * @description Implements functionality for index (casalemedia) header bidding
 * @extends _bidbase
 * @requires module:utils
 */
class _indexbid extends bidbase {
  _config: indexConfig;
  _DefConfig: indexConfig;
  constructor() {
    super('index');
    this._DefConfig = u.extend(true, {}, this._DefConfig, {
      id: 0
    });
    this._config = u.extend(true, {}, this._DefConfig);
    this._renderType = 'func';
  }

  /**
   * - Checks partner specific config for index partner
   * @returns {initStatus} - Init status
   * @access private
   */
  _configCheck(): initStatus {
    //id should be a number (default set to zero) - index javascript checks for id being number
    if (!u.isNumber(this._config.id) || this._config.id === 0) {
      u.logError(`${this._name} config error: missing mandatory data`);
      return u.F_FAILED;
    }

    // index js expect height, weight & siteID as number
    let props = ['height', 'width', 'siteID'];
    let chk = Object.keys(this._config.posTokenMap).every(pos =>
      props.every(prop => {
        if (!u.isNumber(this._config.posTokenMap[pos][prop])) {
          u.logError(
            `${
              this._name
            } config error: posTokenMap for ${pos} for property ${prop} is not number`
          );
          return false;
        }
        return true;
      })
    );

    return chk ? super._configCheck() : u.F_FAILED;
  }

  /**
   * - Sets global index variables
   * @returns {initStatus} - Init status
   * @access private
   */
  _preInit(): initStatus {
    //configure index global params
    u.extend(true, _cygnus_index_args, {
      timeout: this._config.timeout,
      siteID: this._config.id
    });
    return u.F_INITED;
  }

  /**
   * Process bid response. Index bids will be stored in index_slots global array.
   * Format: IOM_<id>_price
   * Sample: ["IOM_46218_44", "IOM_46219_45", "IOM_46216_73", "IOM_46217_48", "IOM_46220_48"]
   * @param {Array<string>} posList - HB Positions list
   * @param {Object.<string, hbPosData>} ret - HB data
   * @return {Object.<string, hbPosData>} - HB data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!u.isFunction(window.index_render)) {
      u.logError(`${this._name} index_render func is not defined`);
      return ret;
    }
    if (!u.isArray(window.index_slots)) {
      u.logError(`${this._name} index_slots response array is not defined`);
      return ret;
    }
    this._renderFunc = window.index_render;
    var res = {},
      index_params = [],
      id = '',
      price = '',
      slotMap = {};
    window.index_slots.forEach(slot => {
      index_params = slot.split('_');
      if (index_params.length !== 3) {
        return;
      }
      id = index_params[1];
      price = index_params[2];
      slotMap[id] = {
        price: parseInt(price, 10),
        slot: `${id}_${price}`
      };
    });
    posList.forEach(pos => {
      id = this.getPosKey(pos);
      res = slotMap[id];
      if (res && res.price && res.slot) {
        this._handleBidPosData(ret, pos, u.centsToDollar(res.price), res.slot);
      }
    });
    return ret;
  }

  /**
   * get bids for given positions
   * @param {Array<string>} posList - HB ad positions list
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Promise<Object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return new Promise(resolve => {
      let loadTimer = setTimeout(() => {
        u.logError(`${this._name} Failed to get bids: timeout`);
        resolve(ret);
      }, this._config.timeout);

      // bid response callback
      _cygnus_index_args.callback = () => {
        clearTimeout(loadTimer);
        loadTimer = null;
        resolve(this._processBidResponse(posList, ret));
      };

      const slotIdMap = posList.reduce((o, v) => {
        o[this.getPosKey(v)] = v;
        return o;
      }, {});
      /* eslint-disable arrow-body-style */
      _cygnus_index_args.slots = Object.keys(slotIdMap).map(k => {
        return {
          id: this.getPosKey(slotIdMap[k]),
          width: this._config.posTokenMap[slotIdMap[k]].width,
          height: this._config.posTokenMap[slotIdMap[k]].height,
          siteID: this._config.posTokenMap[slotIdMap[k]].siteID
        };
      });
      /* eslint-enable arrow-body-style */
      // reset internal index structure
      __IndexRequestData.impIDToSlotID = {};
      __IndexRequestData.reqOptions = {};
      var url: string = _cygnus_index_start();
      u.loadJS(url, document, true);
    });
  }
}

//add bidder to bidmanager
let indexbid = new _indexbid();
bm.addBidder('index', indexbid);
export default indexbid;
